import { MutatedExperimentVariant } from 'modules/experiments/takeover/types';
import {
  DeleteCardProgress,
  SelectVariantMode,
  SubmitButtonType,
  VariantCardContextState,
  VariantFieldValues,
} from 'modules/experiments/types';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { FC, useCallback, useState } from 'react';

import { useSelector } from 'react-redux';
import { getTakeoverExperimentLocale } from '../takeover/ducks/selectors';
import { useTakeoverExperimentContext } from './TakeoverExperimentContext';
import { VariantCardContext } from './VariantCardContext';
import { useVariantConfigurationContext } from './VariantConfigurationContext';
import { useVariantTemplateValidationContext } from './VariantTemplateValidationContext';

type VariantCardContextProviderProps = {
  cardIndex: number;
  variant: MutatedExperimentVariant;
};

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const VariantCardContextProvider: FC<
  React.PropsWithChildren<VariantCardContextProviderProps>
> = ({ cardIndex, variant, children }) => {
  const [deletingCard, setDeletingCard] = useState(DeleteCardProgress.NOT_STARTED);
  const [updatingCard, setUpdatingCard] = useState(false);
  const [renamingCard, setRenamingCard] = useState(false);
  const {
    selectedVariant,
    selectMode,
    setDeletingActiveVariant,
    deleteExperimentVariant,
    setEditSelectMode,
    setPreviewSelectMode,
    getPrecedingVariant,
    updateExperimentVariant,
    renameExperimentVariant,
  } = useVariantConfigurationContext();
  const {
    unsavedChanges: { variantList: unsavedVariant, unsavedVariantModal },
  } = useTakeoverExperimentContext();
  const {
    currentLocalesTemplateError,
    deleteTemplateErrorsForAllVariantLocales,
    updateTemplateErrorsForAllVariantLocales,
  } = useVariantTemplateValidationContext();
  const { setTemplateId: setTemplatePreviewId } = useQueryParamContext();
  const currentLocale = useSelector(getTakeoverExperimentLocale);

  const { settingsLocaleContentErrors = [], settingsLocaleContentWarnings = [] } =
    currentLocalesTemplateError[variant.id] || {};

  const isCardSelected = variant.id === selectedVariant?.id;

  const deleteCard = useCallback(async () => {
    setDeletingActiveVariant(isCardSelected);
    setDeletingCard(DeleteCardProgress.DELETING);
    const isDeleteSuccess = await deleteExperimentVariant(
      {
        variantId: variant.id,
        variantName: variant.name,
        onSuccess: async () => {
          deleteTemplateErrorsForAllVariantLocales(variant.id);
          setDeletingCard(DeleteCardProgress.ENDED);
          // We add a little delay to give time for the
          // css fade out animation to play out.
          await delay(500);
        },
      },
      currentLocale,
    );
    if (isDeleteSuccess && isCardSelected) {
      setPreviewSelectMode(getPrecedingVariant(variant.id));
    }

    // Reset if the delete fails
    if (!isDeleteSuccess) setDeletingCard(DeleteCardProgress.NOT_STARTED);
    // Reset card deleting flag
    setDeletingActiveVariant(false);
  }, [variant, isCardSelected]);

  const editCard = useCallback(() => {
    setEditSelectMode(variant);
    setTemplatePreviewId(variant.id);
  }, [variant]);

  const renameCard = useCallback(
    async (newVariantName: string) => {
      setRenamingCard(true);
      await renameExperimentVariant(
        { variantId: variant.id, variantName: newVariantName },
        currentLocale,
      );
      setRenamingCard(false);
    },
    [variant],
  );

  const selectCard = useCallback(() => {
    setPreviewSelectMode(variant);
    setTemplatePreviewId(variant.id);
  }, [variant]);

  const updateCard = useCallback(
    async (variantFieldValues: VariantFieldValues, submitType: SubmitButtonType) => {
      setUpdatingCard(true);
      const response = await updateExperimentVariant(variant.id, variantFieldValues);
      if (response.isSuccess) {
        /**
         * Set the current variant card to preview mode
         * only when the user is simply updating the card
         * without adding a new variant
         */
        if (submitType !== SubmitButtonType.SUBMIT_AND_ADD_VARIANT) {
          setPreviewSelectMode(response.mutatedVariant);
        }

        // Do a BE validation on the new change
        updateTemplateErrorsForAllVariantLocales(response.mutatedVariant.id);
      }
      setUpdatingCard(false);
      return response;
    },
    [variant],
  );

  const value: VariantCardContextState = {
    cardIndex,
    variant,
    templateErrors: [...settingsLocaleContentErrors, ...settingsLocaleContentWarnings],
    deletingCard,
    updatingCard,
    renamingCard,
    isCardSelected,
    isCardInEditMode: isCardSelected && selectMode === SelectVariantMode.EDIT,
    isCardInPreviewMode: isCardSelected && selectMode === SelectVariantMode.PREVIEW,
    deleteCard,
    editCard: unsavedVariant ? () => unsavedVariantModal(editCard) : editCard,
    renameCard,
    selectCard: unsavedVariant ? () => unsavedVariantModal(selectCard) : selectCard,
    updateCard,
  };

  return <VariantCardContext.Provider value={value}>{children}</VariantCardContext.Provider>;
};
